import { isAfter, startOfToday } from 'date-fns';
import { ServiceCount } from 'src/models/package/_types';
import { User } from 'src/models/user/_types';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { formatMonthDayYear } from 'src/utils/dateFormatter';

import { UserPackage } from './user-package';

export interface VisitDetail {
  id: Identifier;
  date: number;
  serviceName: string;
  status: string;
}

export interface VisitInterval {
  serviceCountIndex: number;
  startDate: string;
  endDate: string;
  maxVisits: number;
  used: number;
  remaining: number;
  isCurrent: boolean;
  visits: VisitDetail[];
}

export const fetchVisitIntervals = async (
  userPackageId: Identifier
): Promise<VisitInterval[]> => {
  const { data } = await api.get(
    `/api/v4/fbm/user_packages/${userPackageId}/visit_intervals`
  );
  return (data.intervals || []).map((i: any) => ({
    serviceCountIndex: i.service_count_index,
    startDate: i.start_date,
    endDate: i.end_date,
    maxVisits: i.max_visits,
    used: i.used,
    remaining: i.remaining,
    isCurrent: i.is_current,
    visits: (i.visits || []).map((v: any) => ({
      id: v.id,
      date: v.date,
      serviceName: v.service_name,
      status: v.status,
    })),
  }));
};

export const getPackageDateRange = (userPackage: UserPackage) => {
  return `${formatMonthDayYear(userPackage.activationDate)} - ${
    userPackage.expiresOn
      ? formatMonthDayYear(userPackage.expiresOn)
      : 'Forever'
  }`;
};

export const getPackageVisitsUsed = (userPackage: UserPackage) => {
  return userPackage.serviceCounts.reduce(
    (acc, serviceCount) => acc + serviceCount.used,
    0
  );
};

export const isServiceCountUnlimited = (serviceCount: ServiceCount) => {
  return serviceCount.count == 0 && !hasIntervalLimit(serviceCount);
};

export const hasIntervalLimit = (serviceCount: ServiceCount) => {
  return (
    !!serviceCount.maxVisits &&
    !!serviceCount.visitsInterval &&
    !!serviceCount.visitsIntervalCount
  );
};

/**
 * Subscription packages created before `cadence` could have both a total visit
 * cap (`count`) and a per-interval booking limit (`maxVisits` + interval).
 */
export const isLegacyMixedSubscriptionVisits = (
  serviceCount: ServiceCount
) => {
  const cadence = serviceCount.cadence as string | null | undefined;
  if (cadence != null && cadence !== '') return false;
  return (serviceCount.count > 0 || serviceCount.count === undefined || serviceCount.count === null || String(serviceCount.count) === '') && hasIntervalLimit(serviceCount);
};

const INTERVAL_LABELS: Record<string, { one: string; other: string }> = {
  day: { one: 'day', other: 'days' },
  week: { one: 'week', other: 'weeks' },
  month: { one: 'month', other: 'months' },
  year: { one: 'year', other: 'years' },
};

export const formatIntervalLimit = (serviceCount: ServiceCount) => {
  const interval = INTERVAL_LABELS[serviceCount.visitsInterval];
  if (!interval) return '';
  const intervalLabel =
    serviceCount.visitsIntervalCount === 1 ? interval.one : interval.other;
  return `${serviceCount.maxVisits} / ${serviceCount.visitsIntervalCount} ${intervalLabel}`;
};

export const getRemainingVisits = (
  serviceCount: ServiceCount
): number | null => {
  if (!hasIntervalLimit(serviceCount)) return null;
  return Math.max(0, serviceCount.maxVisits - serviceCount.used);
};

export const isUserPackageExpired = (userPackage: UserPackage) => {
  const today = startOfToday();

  if (!userPackage.expiresOn) {
    return false;
  } else {
    return isAfter(today, userPackage.expiresOn);
  }
};

export const isPackageShareable = (userPackage: UserPackage) => {
  return userPackage.maxShares > 0;
};

export const isPackageShared = (userPackage: UserPackage) => {
  return userPackage.userIds.length > 1;
};

export const isPackageOwner = (userPackage: UserPackage, user: User) => {
  return getPackageOwnerId(userPackage) == user.id;
};

export const getPackageOwnerId = (userPackage: UserPackage) => {
  return userPackage.ownerId || userPackage.userIds.slice(-1)[0];
};

export const isPackageSigned = (userPackage: UserPackage) => {
  return userPackage.signature != null;
};

/** Meta-backed booleans are often stored as the string `'true'`. */
export const isMetaTruthy = (value: unknown) =>
  value === true || value === 'true';
